import React from 'react';
import { Cookie, ArrowLeft, Settings, Shield, BarChart3, Target } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';

const CookiesPolicy = () => {
  const cookieTypes = [
    {
      icon: Shield,
      title: 'Essential Cookies',
      description: 'Required for the website to function properly. These cannot be disabled.',
      examples: ['Session management', 'Security tokens', 'Cookie consent preferences'],
      canDisable: false
    },
    {
      icon: BarChart3,
      title: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website.',
      examples: ['Google Analytics', 'Page view tracking', 'User journey analysis'],
      canDisable: true
    },
    {
      icon: Settings,
      title: 'Functional Cookies',
      description: 'Remember your preferences and settings for a better experience.',
      examples: ['Language preferences', 'Form auto-fill data', 'Theme settings'],
      canDisable: true
    },
    {
      icon: Target,
      title: 'Marketing Cookies',
      description: 'Used to deliver relevant advertisements and track campaign effectiveness.',
      examples: ['Facebook Pixel', 'Google Ads remarketing', 'Social media integrations'],
      canDisable: true
    }
  ];

  const sections = [
    {
      title: '1. What Are Cookies?',
      content: `Cookies are small text files that are placed on your device (computer, smartphone, or tablet) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.

Cookies can be:
• Session cookies: Temporary cookies that expire when you close your browser
• Persistent cookies: Remain on your device for a set period or until you delete them
• First-party cookies: Set by the website you are visiting
• Third-party cookies: Set by external services used on the website`
    },
    {
      title: '2. How We Use Cookies',
      content: `Rocket Van Express uses cookies to:
• Ensure our website functions correctly
• Remember your preferences and settings
• Understand how you use our website (analytics)
• Improve our services based on usage data
• Provide relevant content and advertisements
• Enable social media sharing features
• Process booking and quote requests efficiently`
    },
    {
      title: '3. Your Cookie Choices',
      content: `When you first visit our website, you will see a cookie consent banner that allows you to:
• Accept all cookies
• Manage your preferences (choose which types to allow)
• Reject non-essential cookies

You can change your cookie preferences at any time by:
• Clicking the "Cookie Settings" link in the website footer
• Adjusting your browser settings
• Clearing your browser's cookies and cache

Note: Disabling certain cookies may affect website functionality.`
    },
    {
      title: '4. Browser Cookie Settings',
      content: `Most web browsers allow you to control cookies through their settings. Here's how to manage cookies in popular browsers:

Google Chrome: Settings → Privacy and Security → Cookies and other site data
Mozilla Firefox: Settings → Privacy & Security → Cookies and Site Data
Safari: Preferences → Privacy → Manage Website Data
Microsoft Edge: Settings → Cookies and site permissions → Manage and delete cookies

For detailed instructions, visit your browser's help documentation.`
    },
    {
      title: '5. Third-Party Cookies',
      content: `We use the following third-party services that may set cookies:

Google Analytics:
• Purpose: Website analytics and performance tracking
• Data collected: Page views, session duration, device information
• Retention: 26 months
• Privacy policy: https://policies.google.com/privacy

Google Maps:
• Purpose: Display interactive maps of our service areas
• Privacy policy: https://policies.google.com/privacy

WhatsApp (Meta):
• Purpose: Enable direct messaging functionality
• Privacy policy: https://www.whatsapp.com/legal/privacy-policy

Social Media Platforms:
• Purpose: Social sharing and integration features
• Platforms: Facebook, Instagram, LinkedIn, TikTok`
    },
    {
      title: '6. POPIA & ECTA Compliance',
      content: `In accordance with the Protection of Personal Information Act (POPIA), Act 4 of 2013, and the Electronic Communications and Transactions Act (ECTA), Act 25 of 2002, we ensure that:
- You are informed about our cookie usage before any non-essential cookies are set
- You have the ability to consent to or refuse non-essential cookies
- Your cookie preferences are respected and stored securely
- We only collect data necessary for the stated purposes (data minimisation)
- You can withdraw consent at any time without detriment
- Your data is handled securely and responsibly
- Cookie data is processed in accordance with POPIA's conditions for lawful processing
- Any personal data collected via cookies is subject to the same rights and protections as outlined in our Privacy Policy

Your Information Officer can be contacted at privacy@rocketvan.co.za for any cookie-related data subject requests.`
    },
    {
      title: '7. Data Retention',
      content: `Cookie data is retained as follows:
• Session cookies: Deleted when you close your browser
• Analytics cookies: 26 months (Google Analytics default)
• Preference cookies: 12 months
• Marketing cookies: As per third-party provider policies

You can delete cookies at any time through your browser settings.`
    },
    {
      title: '8. Updates to This Policy',
      content: `We may update this Cookie Policy from time to time to reflect:
• Changes in our cookie usage
• New legal requirements
• Updates to third-party services

The "Last updated" date at the top of this page indicates when the policy was last revised. We encourage you to review this policy periodically.`
    },
    {
      title: '9. Contact Us',
      content: `If you have questions about our use of cookies or this policy, please contact us:

Information Officer: privacy@rocketvan.co.za
General: Info@rocketvan.co.za
WhatsApp: +27 736596239

For general privacy inquiries or to exercise your rights as a data subject under POPIA, please refer to our Privacy Policy or contact our Information Officer directly.`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center text-gray-300 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <Cookie className="h-12 w-12 text-[#039fff] mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Cookie Policy</h1>
            <p className="text-gray-300">POPIA & ECTA Compliant | Last updated: February 15, 2026</p>
          </div>
        </div>
      </section>

      {/* Cookie Types Summary */}
      <section className="py-8 bg-[#039fff]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-[#001A47] mb-6 text-center">Types of Cookies We Use</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {cookieTypes.map((type, index) => {
                const Icon = type.icon;
                return (
                  <Card key={index} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="bg-[#039fff]/10 rounded-full p-2 flex-shrink-0">
                          <Icon className="h-5 w-5 text-[#039fff]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h3 className="font-semibold text-[#001A47]">{type.title}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              type.canDisable 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {type.canDisable ? 'Optional' : 'Required'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{type.description}</p>
                          <div className="flex flex-wrap gap-1">
                            {type.examples.map((example, i) => (
                              <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                {example}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-600 mb-8">
                This Cookie Policy explains how Rocket Van Express (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) uses cookies and similar 
                technologies on our website. This policy should be read alongside our Privacy Policy, 
                which explains how we handle personal information.
              </p>

              <div className="space-y-8">
                {sections.map((section, index) => (
                  <div key={index} id={`section-${index + 1}`} className="scroll-mt-20">
                    <h2 className="text-2xl font-bold text-[#001A47] mb-4">{section.title}</h2>
                    <div className="text-gray-700 whitespace-pre-line leading-relaxed">
                      {section.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 bg-[#fb6402]/5 rounded-lg border-l-4 border-[#fb6402]">
                <h3 className="font-bold text-[#001A47] mb-2">Manage Your Preferences</h3>
                <p className="text-gray-700 mb-4">
                  You can manage your cookie preferences at any time. Click the button below to 
                  update your settings.
                </p>
                <Button 
                  variant="outline" 
                  className="border-[#fb6402] text-[#fb6402] hover:bg-[#fb6402] hover:text-white"
                  onClick={() => {
                    // Trigger cookie consent modal
                    localStorage.removeItem('cookie_consent');
                    window.location.reload();
                  }}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Cookie Settings
                </Button>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/privacy">
                  <Button variant="outline">Privacy Policy</Button>
                </Link>
                <Link to="/terms">
                  <Button variant="outline">Terms & Conditions</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">Contact Us</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiesPolicy;
